import api from '../api';

/** One inventory row as returned by the backend proxy. */
export interface StockDharmaStockRow {
  id: string;
  name: string;
  category?: string | null;
  instock?: number | string | null;
  parlevel?: number | string | null;
  cost?: number | string | null;
  baseunit?: string | null;
  unit?: string | null;
}

/** Shape of `/api/stock-dharma/dashboard/snapshot`. */
export interface StockDharmaSnapshot {
  locationId: string;
  locationName: string | null;
  itemCount: number;
  inventoryValue: number;
  lowStock: StockDharmaStockRow[];
  outOfStock: StockDharmaStockRow[];
  openRequisitions: Array<Record<string, unknown>>;
  pendingDeliveries: Array<Record<string, unknown>>;
  latestSales: Record<string, unknown> | null;
  latestCountSession: Record<string, unknown> | null;
  generatedAt: string;
}

export interface StockDharmaStatus {
  configured: boolean;
  locationId: string;
}

export interface StockDharmaSyncResult {
  status: string;
  itemsImported: number;
  demoProductsPurged: number;
  locationId: string;
  locationName: string;
  syncedAt: string;
  message: string;
}

export interface StockDharmaOrderSyncResult {
  status: string;
  totalOrdersFetched: number;
  ordersImportedOrUpdated: number;
  newOrders: number;
  locationId: string;
  syncedAt: string;
  message: string;
}

/** Stable error raised for any failed proxy call. */
export class StockDharmaProxyError extends Error {
  readonly code: string | null;
  readonly status: number | null;

  constructor(
    message: string,
    options: { code?: string | null; status?: number | null } = {},
  ) {
    super(message);
    this.name = 'StockDharmaProxyError';
    this.code = options.code ?? null;
    this.status = options.status ?? null;
  }
}

export function isNotConfiguredError(error: unknown): boolean {
  return error instanceof StockDharmaProxyError && error.code === 'not_configured';
}

function toProxyError(error: unknown): StockDharmaProxyError {
  const response = (error as {
    response?: { data?: unknown; status?: number };
  })?.response;
  const data =
    response && typeof response.data === 'object' && response.data !== null
      ? (response.data as Record<string, unknown>)
      : null;

  return new StockDharmaProxyError(
    typeof data?.message === 'string'
      ? data.message
      : error instanceof Error
        ? error.message
        : 'The Stock Dharma request failed.',
    {
      code: typeof data?.code === 'string' ? data.code : null,
      status: typeof response?.status === 'number' ? response.status : null,
    },
  );
}

async function request<T>(path: string, timeoutMs: number): Promise<T> {
  try {
    const response = await api.get(path, { timeout: timeoutMs });
    return response.data as T;
  } catch (error) {
    throw toProxyError(error);
  }
}

/** Server-assembled dashboard snapshot for the configured location. */
export function fetchSnapshot(): Promise<StockDharmaSnapshot> {
  return request<StockDharmaSnapshot>(
    '/stock-dharma/dashboard/snapshot',
    30000,
  );
}

/** Whether the server has Stock Dharma credentials. Never returns them. */
export function fetchStatus(): Promise<StockDharmaStatus> {
  return request<StockDharmaStatus>('/stock-dharma/status', 10000);
}

/** Synchronizes the SIMS product catalog with Stock Dharma. */
export async function syncProducts(purgeDemo = true): Promise<StockDharmaSyncResult> {
  try {
    const response = await api.post<StockDharmaSyncResult>(
      `/stock-dharma/sync-products?purgeExistingDemoData=${purgeDemo}`,
      null,
      { timeout: 60000 }
    );
    return response.data;
  } catch (error) {
    throw toProxyError(error);
  }
}

/** Synchronizes Stock Dharma purchase orders into SIMS. */
export async function syncOrders(): Promise<StockDharmaOrderSyncResult> {
  try {
    const response = await api.post<StockDharmaOrderSyncResult>(
      '/stock-dharma/orders/sync',
      null,
      { timeout: 60000 }
    );
    return response.data;
  } catch (error) {
    throw toProxyError(error);
  }
}
